import { useLayoutEffect, useRef, useState } from 'react'

import { REQUEST_API_ALIASES } from '../../constants/api'
import { EMPTY_LIST, EMPTY_OBJECT, ENTITY_KEY_ID } from '../../constants/common'
import { PAGINATION_ALL_ITEMS_VALUE } from '../../constants/pagination'
import { isApiServerInternalError, postApi } from '../../utils/api'
import {
  getPaginationApiParams,
  updatePaginationCount,
} from '../../utils/pagination'
import { toOrderingList } from '../../utils/sort'
import { useAsyncCallback } from '../useAsyncCallback'

import { getTableLoaderListApiParams } from './useTableLoader.utils'

export function useTableLoader({
  countApi,
  entity,
  getListApiParams = getTableLoaderListApiParams,
  handleTableInit,
  itemIdKey = ENTITY_KEY_ID,
  listApi,
  setPagination,
  onServerInternalError,
}) {
  const [isLoadedInit, setLoadedInit] = useState(false)
  const [isLoadFailed, setLoadFailed] = useState(false)
  const [isLoadingPage, setLoadingPage] = useState(false)
  const [serverError, setServerError] = useState(null)
  const [items, setItems] = useState(null)
  const [permissions, setPermissions] = useState(EMPTY_OBJECT)

  const orderRef = useRef(null)
  const paramsRef = useRef(null)
  const paginationRef = useRef(null)
  const serverInternalErrorHandledRef = useRef(false)
  const handleLoadPageRef = useRef(null)
  const loadGenerationRef = useRef(0)
  const loadPageIdRef = useRef(0)

  async function handleLoadInit(params) {
    loadGenerationRef.current += 1

    const generation = loadGenerationRef.current

    setLoadedInit(false)
    setLoadFailed(false)
    setServerError(null)
    paramsRef.current = params

    const data = await handleTableInit()

    if (generation === loadGenerationRef.current) {
      setLoadedInit(true)
    }

    return data
  }

  // Kept on `useAsyncCallback`: it self-recurses (invalid-pagination retry),
  // drives the page-load state, and is the stable anchor the thin wrappers
  // (`handleReload`, `handleLoadPageInitial`) depend on.
  const handleLoadPage = useAsyncCallback(
    (pagination, order) => {
      const generation = loadGenerationRef.current

      loadPageIdRef.current += 1

      const loadPageId = loadPageIdRef.current

      setLoadingPage(true)

      paginationRef.current = pagination
      // Kept raw on purpose: `loadTableData` compares this ref against the
      // `ordering` state, so normalizing it here would make `[]` never equal a
      // `null` state and refetch on every render.
      orderRef.current = order

      const { countApi: entityCountApi, listApi: entityListApi } =
        REQUEST_API_ALIASES[entity?.single] || EMPTY_OBJECT

      const paginationApiParams = getPaginationApiParams(
        pagination ?? PAGINATION_ALL_ITEMS_VALUE
      )

      const params = {
        ...paramsRef.current,
        ...paginationApiParams,
        ordering: toOrderingList(order),
      }

      const apiParams = getListApiParams(params)

      return Promise.all([
        postApi({ data: apiParams, endpoint: listApi || entityListApi }),
        ...(countApi || entityCountApi
          ? [postApi({ data: apiParams, endpoint: countApi || entityCountApi })]
          : EMPTY_LIST),
      ])
        .then(([entityData, countData]) => {
          // A superseded load (a newer init or page load) still resolves
          // (callers chain spinners on it) but must not write the state of the
          // current one: overlapping reloads may answer in any order.
          if (
            generation !== loadGenerationRef.current ||
            loadPageId !== loadPageIdRef.current
          ) {
            return [entityData, countData]
          }

          serverInternalErrorHandledRef.current = false

          setLoadFailed(false)
          setServerError(null)

          const total = countData
            ? countData.data.total
            : entityData.data.pagination.total

          const pageCount = countData
            ? countData.data.page_count
            : entityData.data.pagination.page_count

          setItems(entityData.data)

          setPermissions(entityData.data.permissions)

          const nextPagination = updatePaginationCount(
            { ...pagination, pageCount },
            total
          )

          // This may happen if user set not invalid page and page size
          if (nextPagination.page !== pagination.page) {
            throw { validPagination: nextPagination }
          }

          paginationRef.current = nextPagination
          setPagination?.(nextPagination)

          return [entityData, countData]
        })
        .catch(error => {
          if (
            generation !== loadGenerationRef.current ||
            loadPageId !== loadPageIdRef.current
          ) {
            return
          }

          if (error.validPagination) {
            // Load data if were invalid pagination params
            return handleLoadPageRef.current(
              error.validPagination,
              orderRef.current
            )
          }

          //if we got 500 from server -> reset the filters and try to request list again
          // If there is still an error -> display error page
          if (
            isApiServerInternalError(error?.response?.status) &&
            !serverInternalErrorHandledRef.current
          ) {
            serverInternalErrorHandledRef.current = true

            // Not settled on purpose: the reset re-runs the load through the
            // init effect, which marks the table initiated once the retry lands.
            Promise.resolve(onServerInternalError?.()).catch(() => {
              // A failed reset brings no retry, so settle as a failed load.
              if (generation === loadGenerationRef.current) {
                setLoadFailed(true)
                setServerError(error?.response)
              }
            })

            return
          }

          setLoadFailed(true)
          setServerError(error?.response)
        })
        .finally(() => {
          if (
            generation === loadGenerationRef.current &&
            loadPageId === loadPageIdRef.current
          ) {
            setLoadingPage(false)
          }
        })
    },
    [
      countApi,
      entity,
      getListApiParams,
      listApi,
      onServerInternalError,
      setPagination,
    ]
  )

  useLayoutEffect(() => {
    handleLoadPageRef.current = handleLoadPage
  }, [handleLoadPage])

  async function handleLoadPageInitial(params, pagination, order) {
    paramsRef.current = params

    return handleLoadPage(pagination, order)
  }

  async function handleReload() {
    // No page has been loaded yet, or an init (a sub-tab switch) is in flight
    // and has dropped the params — bail instead of loading without filters; the
    // initial load brings fresh data.
    if (paginationRef.current == null || paramsRef.current == null) {
      return
    }

    return handleLoadPage(paginationRef.current, orderRef.current)
  }

  function handleUpdateItem({ id, updates }) {
    setItems(prev => ({
      ...prev,
      items: prev.items.map(item =>
        item[itemIdKey] === id ? { ...item, ...updates } : item
      ),
    }))
  }

  return {
    handleLoadInit,
    handleLoadPage,
    handleLoadPageInitial,
    handleReload,
    handleUpdateItem,
    isLoadedInit,
    isLoadFailed,
    isLoadingPage,
    items,
    loadGenerationRef,
    orderRef,
    paginationRef,
    paramsRef,
    permissions,
    serverError,
  }
}
